import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth, useTheme, API } from '../App';
import { toast } from 'sonner';
import { 
  CircleDot, Sun, Moon, ArrowLeft, Coins, Check, Sparkles,
  CreditCard, Package, Zap, History, ShieldCheck, AlertTriangle
} from 'lucide-react';
import CreditHistory from '../components/CreditHistory';

const PACKAGES = [
  {
    id: 'starter',
    name: 'Starter Pack',
    credits: 10,
    price: 5,
    description: 'Perfect for trying out',
    icon: <Package className="w-6 h-6" />,
    popular: false,
  },
  {
    id: 'creator',
    name: 'Creator Pack',
    credits: 30,
    price: 12,
    description: 'Best value for creators',
    icon: <Zap className="w-6 h-6" />,
    popular: true,
  },
  {
    id: 'unlimited',
    name: 'Unlimited Pack',
    credits: 100,
    price: 35,
    description: 'For serious creators',
    icon: <Sparkles className="w-6 h-6" />,
    popular: false,
  },
];

const Credits = () => {
  const { user, token, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('buy'); // 'buy' or 'history'

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh.');
      setIsProcessing(false);
      return;
    }

    // Check if we have a valid token
    if (!token) {
      toast.error('Session expired. Please log in again to verify your payment.');
      setIsProcessing(false);
      return;
    }

    setIsProcessing(true);

    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      // Handle auth errors
      if (res.status === 401 || res.status === 403) {
        toast.error('Session expired. Please log in again to verify your payment.');
        setIsProcessing(false);
        // Clear URL params so user doesn't get stuck in loop
        window.history.replaceState({}, '', '/credits');
        return;
      }

      if (res.ok) {
        const data = await res.json();
        
        if (data.payment_status === 'paid') {
          setPurchaseComplete(true);
          setIsProcessing(false);
          // Refresh user credits
          const userRes = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          });
          if (userRes.ok) {
            const userData = await userRes.json();
            updateUser({ credits: userData.credits });
          }
          toast.success(`${data.credits_added} credits added!`);
          // Clear the URL params
          window.history.replaceState({}, '', '/credits');
          return;
        } else if (data.status === 'expired') {
          toast.error('Payment session expired');
          setIsProcessing(false);
          return;
        }
      } else if (res.status === 404) {
        // Transaction not found - might be a different user's session
        toast.error('Payment session not found. Please try again.');
        setIsProcessing(false);
        window.history.replaceState({}, '', '/credits');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Payment status check error:', err);
      // If we get a 401, the token is invalid - stop polling
      if (err.message?.includes('401') || err.message?.includes('unauthorized')) {
        toast.error('Session expired. Please log in again.');
        setIsProcessing(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!token) {
      toast.error('Please log in to purchase credits');
      return;
    }
    
    setIsProcessing(true);

    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });

      if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        setIsProcessing(false);
        return;
      }

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create checkout');
      }

      const data = await res.json();
      
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Gallery</span>
          </Link>

          <div className="flex items-center gap-2">
            <CircleDot className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl">Credits</span>
          </div>

          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleTheme}
            data-testid="credits-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Current Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 mb-8 text-center"
          >
            <p className="text-muted-foreground mb-2">Your Current Balance</p>
            <div className="flex items-center justify-center gap-3">
              <Coins className="w-10 h-10 text-primary" />
              <span className="text-6xl font-heading" data-testid="current-credits">{user?.credits || 0}</span>
              <span className="text-2xl text-muted-foreground">credits</span>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              1 credit = 1 sacred geometry generation
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-8">
            <Button
              variant={activeTab === 'buy' ? 'default' : 'outline'}
              onClick={() => setActiveTab('buy')}
              className="rounded-full"
              data-testid="tab-buy"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Buy Credits
            </Button>
            <Button
              variant={activeTab === 'history' ? 'default' : 'outline'}
              onClick={() => setActiveTab('history')}
              className="rounded-full"
              data-testid="tab-history"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>

          {/* Success Message */}
          {purchaseComplete && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-6 mb-8 border-green-500/50 bg-green-500/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl">Purchase Complete!</h3>
                  <p className="text-muted-foreground">Your credits have been added to your account.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Processing Message */}
          {isProcessing && !purchaseComplete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass-card p-6 mb-8"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <div>
                  <h3 className="font-heading text-xl">Processing Payment...</h3>
                  <p className="text-muted-foreground">Please wait while we verify your payment.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Buy Credits Tab */}
          {activeTab === 'buy' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h2 className="font-heading text-3xl text-center mb-2">Buy Credits</h2>
              <p className="text-center text-muted-foreground mb-10">
                Choose a package that fits your creative needs
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {PACKAGES.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`glass-card p-8 relative ${pkg.popular ? 'border-primary/50 ring-2 ring-primary/20' : ''}`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full uppercase tracking-wide">
                        Best Value
                      </div>
                    )}

                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 text-primary">
                      {pkg.icon}
                    </div>

                    <h3 className="font-heading text-2xl mb-1">{pkg.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-4xl font-heading">${pkg.price}</span>
                      <span className="text-muted-foreground">/ {pkg.credits} credits</span>
                    </div>

                    <div className="text-sm text-muted-foreground mb-6">
                      ${(pkg.price / pkg.credits).toFixed(2)} per generation
                    </div>

                    <Button
                      className={`w-full rounded-full ${pkg.popular ? 'glow-primary' : ''}`}
                      variant={pkg.popular ? 'default' : 'outline'}
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={isProcessing}
                      data-testid={`buy-${pkg.id}-btn`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Buy Now
                    </Button>
                  </motion.div>
                ))}
              </div>

              {/* Info */}
              <div className="text-center text-sm text-muted-foreground mt-8">
                <p>Secure payment powered by Stripe. Credits never expire.</p>
              </div>

              {/* Refund Policy */}
              <div className="mt-12 border-t border-border pt-8 max-w-3xl mx-auto" data-testid="refund-policy">
                <h3 className="font-heading text-lg mb-4 text-center text-foreground/80">Refund Policy</h3>
                
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div className="flex gap-3">
                    <ShieldCheck className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground/70 mb-1">Eligible for Refund or Account Credit</p>
                      <p>
                        We will issue a refund or account credit if our engine fails to deliver the promised product due to a technical failure on our end. This includes:
                      </p>
                      <ul className="list-disc list-inside mt-1.5 space-y-1 pl-1">
                        <li><span className="text-foreground/60 font-medium">Corrupted Files</span> — The image generates, but the file is broken, won't open, or exports as a blank screen.</li>
                        <li><span className="text-foreground/60 font-medium">Server Timeouts</span> — You use a generation credit, but the engine times out and never delivers the image.</li>
                        <li><span className="text-foreground/60 font-medium">System Bugs</span> — The engine completely ignores the parameters you set due to a verifiable backend error (e.g., you selected a vector export and the system forced a low-res JPEG).</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground/70 mb-1">Not Eligible for Refund</p>
                      <p>
                        Due to the unpredictable and subjective nature of generative art, we do not offer refunds based on aesthetic preferences, slight imperfections, or the art not matching your personal vision. Each generation is a unique creation, and variation is inherent to the process.
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground/70 text-center pt-2">
                    To request a refund for a qualifying technical issue, please use the bug report button or contact us with details of the error.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CreditHistory />
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Credits;
